'use client';

import { Plus, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Fragment, useId } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import {
  appendStudioAction,
  MAX_STUDIO_ACTION_TEXT_LENGTH,
  MIN_STUDIO_ACTION_DURATION,
  type StudioAction,
  type StudioActionIssue,
} from '../_hooks';

interface InlineActionTimelineProps {
  actions: StudioAction[];
  duration?: number;
  issues: StudioActionIssue[];
  onChange: (id: string, patch: Partial<StudioAction>) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
}

function hasValidRange(action: StudioAction) {
  return Number.isFinite(action.start) && Number.isFinite(action.end) && action.start >= 0 && action.end > action.start;
}

function isActionTimeInvalid(
  action: StudioAction,
  actions: StudioAction[],
  duration: number | undefined,
  issues: StudioActionIssue[],
) {
  if (issues.includes('range') && !hasValidRange(action)) return true;
  if (
    issues.includes('exceeds') &&
    duration !== undefined &&
    ((Number.isFinite(action.start) && action.start > duration) ||
      (Number.isFinite(action.end) && action.end > duration))
  ) {
    return true;
  }
  return (
    issues.includes('overlap') &&
    hasValidRange(action) &&
    actions.some(
      (other) => other.id !== action.id && hasValidRange(other) && action.start < other.end && other.start < action.end,
    )
  );
}

export function InlineActionTimeline({
  actions,
  duration,
  issues,
  onChange,
  onDelete,
  onAdd,
}: InlineActionTimelineProps) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  const groupId = useId();
  const canAdd =
    duration === undefined || appendStudioAction(actions, '__availability__', duration).length > actions.length;
  const addUnavailableReason =
    duration !== undefined && duration < MIN_STUDIO_ACTION_DURATION
      ? t('actions.durationTooShort')
      : !canAdd
        ? t('actions.noAvailableRange')
        : null;
  const sortedActions = [...actions].sort((first, second) => first.start - second.start);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div data-action-timeline-scroll className="min-h-0 flex-1 overflow-y-auto px-6 py-5 md:px-4 md:py-4">
        {duration === undefined ? <p className="mb-4 text-sm text-system-white64">{t('actions.textTiming')}</p> : null}
        <div className="space-y-3">
          {sortedActions.map((action, index) => (
            <div key={action.id} className="relative px-11 sm:px-0 sm:pt-10">
              <span className="-translate-y-1/2 absolute top-1/2 left-0 flex size-8 items-center justify-center rounded-full border border-system-white16 bg-system-white08 font-semibold text-sm text-system-white80 tabular-nums shadow-sm sm:top-0 sm:left-4 sm:translate-y-0">
                {String(index + 1).padStart(2, '0')}
              </span>
              <CustomTooltip
                content={sortedActions.length === 1 ? t('actions.keepOneAction') : null}
                trigger="click"
                side="top"
                sideOffset={8}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (sortedActions.length > 1) onDelete(action.id);
                  }}
                  aria-label={t('actions.remove')}
                  className="-translate-y-1/2 absolute top-1/2 right-0 flex size-8 items-center justify-center rounded-full border border-system-white12 bg-system-white04 text-system-white48 shadow-sm transition-colors hover:border-system-white24 hover:bg-system-white08 hover:text-system-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 motion-reduce:transition-none sm:top-0 sm:right-4 sm:translate-y-0"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </button>
              </CustomTooltip>
              <div className="rounded-16 border border-system-white10 bg-system-white03 p-4 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
                <div className="mb-4 grid grid-cols-[minmax(0,1fr)_32px_minmax(0,1fr)] items-center rounded-12 border border-system-white12 bg-black/15 p-1 transition-colors focus-within:bg-system-white04 has-[input[aria-invalid=true]]:border-red-400/60 has-[input[aria-invalid=true]]:bg-red-400/5 sm:grid-cols-1 sm:gap-1">
                  {(['start', 'end'] as const).map((field, fieldIndex) => (
                    <Fragment key={field}>
                      {fieldIndex > 0 ? (
                        <span className="flex size-8 items-center justify-center sm:mx-auto">
                          <span className="h-px w-4 bg-system-white24" aria-hidden="true" />
                        </span>
                      ) : null}
                      <label className="flex h-11 min-w-0 items-center gap-3 px-3">
                        <span className="shrink-0 text-system-white48 text-xs">{t(`actions.${field}`)}</span>
                        <input
                          type="number"
                          min={0}
                          max={duration === undefined ? 0xffffffff : duration}
                          step={1}
                          value={Number.isFinite(action[field]) ? action[field] : ''}
                          onChange={(event) =>
                            onChange(action.id, {
                              [field]:
                                event.target.value === ''
                                  ? Number.NaN
                                  : Math.min(
                                      Math.max(Math.round(Number(event.target.value)), 0),
                                      duration ?? 0xffffffff,
                                    ),
                            })
                          }
                          aria-invalid={isActionTimeInvalid(action, actions, duration, issues)}
                          className="h-full min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-right font-semibold text-base text-system-white tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <span className="shrink-0 text-system-white40 text-xs">s</span>
                      </label>
                    </Fragment>
                  ))}
                </div>
                <label htmlFor={`${groupId}-${action.id}`} className="mb-2 block text-system-white64 text-xs">
                  {t('actions.prompt')}
                </label>
                <textarea
                  id={`${groupId}-${action.id}`}
                  value={action.prompt}
                  maxLength={MAX_STUDIO_ACTION_TEXT_LENGTH}
                  onChange={(event) =>
                    onChange(action.id, { prompt: event.target.value.slice(0, MAX_STUDIO_ACTION_TEXT_LENGTH) })
                  }
                  placeholder={t('actions.placeholder')}
                  aria-invalid={issues.includes('required') && !action.prompt.trim()}
                  className="h-[88px] max-h-[88px] min-h-[88px] w-full resize-none overflow-y-auto rounded-12 border border-system-white10 bg-black/15 px-3 py-2.5 text-sm text-system-white leading-6 outline-none transition-colors placeholder:text-system-white32 focus:border-system-blue01 focus:ring-1 focus:ring-system-blue01 aria-[invalid=true]:border-red-400/60 aria-[invalid=true]:bg-red-400/5"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="shrink-0 px-[68px] py-4 sm:px-4 md:px-[60px]">
        <CustomTooltip
          content={addUnavailableReason}
          trigger="click"
          side="top"
          sideOffset={8}
          triggerClassName="block w-full"
        >
          <button
            type="button"
            onClick={() => {
              if (!addUnavailableReason) onAdd();
            }}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-12 border border-system-white16 border-dashed font-medium text-sm text-system-white80 transition-colors hover:border-system-white48 hover:bg-system-white04 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 motion-reduce:transition-none"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t('actions.add')}
          </button>
        </CustomTooltip>
      </div>
    </div>
  );
}
